import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, DollarSign, Building, User, Calendar, Send, AlertCircle } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'

interface Ministerio {
  id: string
  nome: string
  descricao: string
  responsavel: string
  status: 'ativo' | 'inativo'
  membros: number
  orcamento: number
  gastoMensal: number
}

// Mock data - em produção viria de uma API
const mockMinisterios: Ministerio[] = [
  {
    id: '1',
    nome: 'Ministério de Louvor',
    descricao: 'Responsável pela música e adoração nos cultos',
    responsavel: 'João Silva',
    status: 'ativo',
    membros: 15,
    orcamento: 5000.00,
    gastoMensal: 1200.00
  },
  {
    id: '2',
    nome: 'Ministério Infantil',
    descricao: 'Cuidado e ensino das crianças',
    responsavel: 'Maria Santos',
    status: 'ativo',
    membros: 8,
    orcamento: 2000.00,
    gastoMensal: 800.00
  },
  {
    id: '3',
    nome: 'Ministério de Evangelismo',
    descricao: 'Evangelização e missões',
    responsavel: 'Pedro Oliveira',
    status: 'ativo',
    membros: 12,
    orcamento: 3000.00,
    gastoMensal: 900.00
  }
]

export function EnviarSaldoPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [ministerio, setMinisterio] = useState<Ministerio | null>(null)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState({
    valor: '',
    descricao: '',
    dataTransferencia: new Date().toISOString().split('T')[0],
    observacoes: ''
  })

  useEffect(() => {
    // Simular carregamento da API
    setTimeout(() => {
      const ministerioEncontrado = mockMinisterios.find(m => m.id === id)
      setMinisterio(ministerioEncontrado || null)
      setLoading(false)
    }, 500)
  }, [id])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.valor || !formData.descricao) {
      alert('Por favor, preencha todos os campos obrigatórios.')
      return
    }

    const valor = parseFloat(formData.valor.replace(',', '.'))
    if (isNaN(valor) || valor <= 0) {
      alert('Por favor, insira um valor válido.')
      return
    }

    // Simular envio para API
    console.log('Enviando saldo:', {
      ministerioId: id,
      valor,
      descricao: formData.descricao,
      dataTransferencia: formData.dataTransferencia,
      observacoes: formData.observacoes
    })

    alert(`Saldo de ${formatCurrency(valor)} enviado com sucesso para ${ministerio?.nome}!`)
    navigate('/financas/solicitacoes-ministeriais')
  }

  const saldoDisponivel = ministerio ? ministerio.orcamento - ministerio.gastoMensal : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando ministério...</p>
        </div>
      </div>
    )
  }

  if (!ministerio) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Ministério não encontrado</h2>
          <p className="text-muted-foreground mb-4">O ministério que você está procurando não existe.</p>
          <Button onClick={() => navigate('/financas/solicitacoes-ministeriais')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/financas/solicitacoes-ministeriais')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Enviar Saldo</h1>
            <p className="text-muted-foreground">Transferir verba para {ministerio.nome}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações do Ministério */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Informações do Ministério
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Nome</label>
              <p className="text-lg font-semibold">{ministerio.nome}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Descrição</label>
              <p className="text-sm">{ministerio.descricao}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Responsável</label>
              <p className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {ministerio.responsavel}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="mt-1">
                <Badge className={ministerio.status === 'ativo' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}>
                  {ministerio.status === 'ativo' ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Membros</label>
              <p className="text-lg font-semibold">{ministerio.membros}</p>
            </div>
          </CardContent>
        </Card>

        {/* Informações Financeiras */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Situação Financeira
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Orçamento Total</label>
              <p className="text-lg font-semibold text-blue-600">{formatCurrency(ministerio.orcamento)}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Gasto Mensal</label>
              <p className="text-lg font-semibold text-orange-600">{formatCurrency(ministerio.gastoMensal)}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Saldo Disponível</label>
              <p className="text-xl font-bold text-green-600">{formatCurrency(saldoDisponivel)}</p>
            </div>
            
            <div className="pt-2 border-t">
              <div className="flex justify-between text-sm">
                <span>Utilização do Orçamento</span>
                <span>{((ministerio.gastoMensal / ministerio.orcamento) * 100).toFixed(1)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 mt-1">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${Math.min((ministerio.gastoMensal / ministerio.orcamento) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Formulário de Envio */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Transferir Saldo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="valor">Valor a Transferir *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">R$</span>
                  <Input
                    id="valor"
                    type="text"
                    placeholder="0,00"
                    value={formData.valor}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d,]/g, '')
                      setFormData({ ...formData, valor: value })
                    }}
                    className="pl-10"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Saldo disponível: {formatCurrency(saldoDisponivel)}
                </p>
              </div>

              <div>
                <Label htmlFor="dataTransferencia">Data da Transferência</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="dataTransferencia"
                    type="date"
                    value={formData.dataTransferencia}
                    onChange={(e) => setFormData({ ...formData, dataTransferencia: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="descricao">Descrição da Transferência *</Label>
              <Input
                id="descricao"
                placeholder="Ex: Verba para compra de equipamentos"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações adicionais sobre a transferência..."
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex space-x-4 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/financas/solicitacoes-ministeriais')}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-green-600 hover:bg-green-700 flex-1"
              >
                <Send className="h-4 w-4 mr-2" />
                Enviar Saldo
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Histórico de Transferências */}
      <Card>
        <CardHeader>
          <CardTitle>Últimas Transferências</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Compra de equipamentos de som</p>
                <p className="text-sm text-muted-foreground">15/01/2024</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-green-600">{formatCurrency(2500.00)}</p>
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Concluída</Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Material para atividades</p>
                <p className="text-sm text-muted-foreground">10/01/2024</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-green-600">{formatCurrency(800.00)}</p>
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Concluída</Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Verba mensal de manutenção</p>
                <p className="text-sm text-muted-foreground">01/01/2024</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-green-600">{formatCurrency(1000.00)}</p>
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Concluída</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default EnviarSaldoPage